import { useDeepCompareMemo } from '@react-hookz/web';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import type { TMessage } from '~entities/messages';
import { useTranslations } from '~i18n/hooks';

const getGroupedMessages = (
	messages: TMessage[],
	t: ReturnType<typeof useTranslations>
) => {
	let lastDate: DateTime | null = null;
	const userFriendlyDates = [t('dateToday'), t('dateYesterday')];

	return messages.reduce((groupedMessages, message, index) => {
		let date = DateTime.fromMillis(message.timestamp);

		// If the date of x more than x + 1, we assign it the date of x - 1.
		if (
			lastDate &&
			index < messages.length - 1 &&
			messages[index + 1] &&
			date > DateTime.fromMillis(messages[index + 1].timestamp)
		) {
			date = lastDate;
		}

		let relativeTime = date.toRelativeCalendar() as string;
		if (!userFriendlyDates.includes(relativeTime)) {
			relativeTime = date.toFormat('ccc d LLL');
		}

		if (!groupedMessages.has(relativeTime)) {
			groupedMessages.set(relativeTime, []);
		}
		groupedMessages.get(relativeTime)?.push(message);

		lastDate = date;
		return groupedMessages;
	}, new Map<string, TMessage[]>());
};

export const useGroupedMessages = (messages: TMessage[]) => {
	const t = useTranslations('Chats');
	const groupedMessages = useMemo(
		() => getGroupedMessages(messages, t),
		[messages] //eslint-disable-line
	);

	const groupNames = useDeepCompareMemo(
		() => Array.from(groupedMessages.keys()),
		[groupedMessages]
	);
	// merge message list with dates to show in a chat
	const mergedMessages: TMessage[] = useMemo(
		() =>
			groupNames.reduce(
				(acc, k) =>
					[
						...acc,
						{ tag: k, id: k },
						...(groupedMessages.get(k) as TMessage[]),
					] as TMessage[],
				[] as TMessage[]
			),
		[groupNames, groupedMessages]
	);
	return { groupNames, mergedMessages };
};
